import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { CameraScreen } from './components/CameraScreen';
import { GalleryScreen } from './components/GalleryScreen';
import { ItemEditor } from './components/ItemEditor';
import { SettingsDialog } from './components/SettingsDialog';
import { db } from './db';
import { useAppCapabilities } from './hooks/useAppCapabilities';
import { useInstallPrompt } from './hooks/useInstallPrompt';
import { groupItemsByLocation } from './lib/groups';
import { normalizePhoto } from './lib/image';
import { searchItems } from './lib/search';
import { clearLocalData, createDraft, deleteItem, renamePlace, saveItem, type PlaceKind } from './services/items';
import { enrichItem, suggestItemNames, suggestLocationGroup } from './services/ai';
import { getSyncConfig } from './services/settings';
import { syncNow } from './services/sync';
import type { EditorMode, ItemRecord } from './types';

type Screen = 'camera' | 'gallery';
type EditorState = { item: ItemRecord; mode: EditorMode } | null;
const demoMode = import.meta.env.DEV && new URLSearchParams(location.search).has('demo');

export default function App() {
  const capabilities = useAppCapabilities();
  const install = useInstallPrompt();
  const items = useLiveQuery(() => db.items.toArray(), [], []);
  const outboxCount = useLiveQuery(() => db.outbox.count(), [], 0);
  const [screen, setScreen] = useState<Screen>(initialScreen);
  const [query, setQuery] = useState('');
  const [grouped, setGrouped] = useState(readGroupingPreference);
  const deferredQuery = useDeferredValue(query);
  const [editor, setEditor] = useState<EditorState>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [syncState, setSyncState] = useState({ configured: true, syncing: false, message: '' });
  const [captureState, setCaptureState] = useState({ busy: false, error: '' });
  const activeSyncs = useRef(0);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const libraryInputRef = useRef<HTMLInputElement>(null);

  const namedItems = useMemo(() => items.filter((item) => item.name.trim()), [items]);
  const nameReview = useMemo(() => items.filter((item) => !item.name.trim()).toSorted((a, b) => a.createdAt.localeCompare(b.createdAt)), [items]);
  const locationReview = useMemo(() => items.filter((item) => item.name.trim() && !item.locationNote.trim()).toSorted((a, b) => a.createdAt.localeCompare(b.createdAt)), [items]);
  const knownGroups = useMemo(() => [...new Set(items.flatMap((item) => item.locationGroup.trim() ? [item.locationGroup.trim()] : []))].toSorted((a, b) => a.localeCompare(b)), [items]);
  const matchingItems = useMemo(() => searchItems(namedItems, deferredQuery), [deferredQuery, namedItems]);
  const sections = useMemo(() => matchingItems.length ? (grouped ? groupItemsByLocation(matchingItems) : [{ title: '', items: matchingItems }]) : [], [grouped, matchingItems]);

  const refreshSyncConfig = useCallback(async () => {
    const config = await getSyncConfig();
    const configured = Boolean(config.endpoint && config.token);
    setSyncState((current) => ({ ...current, configured }));
    return configured;
  }, []);

  const runSync = useCallback(async (interactive = false) => {
    if (demoMode) {
      if (interactive) setSyncState((current) => ({ ...current, message: 'Demo items stay in this browser.' }));
      return;
    }
    const config = await getSyncConfig();
    const configured = Boolean(config.endpoint && config.token);
    setSyncState((current) => ({ ...current, configured }));
    if (!configured) {
      setSyncState((current) => ({ ...current, message: 'Saved here. Family sync needs an app update.' }));
      return;
    }
    if (!navigator.onLine) {
      setSyncState((current) => ({ ...current, message: 'Offline. Changes will sync automatically when this device reconnects.' }));
      return;
    }

    activeSyncs.current += 1;
    setSyncState((current) => ({ ...current, syncing: true, message: interactive ? 'Syncing household changes…' : current.message }));
    try {
      const summary = await syncNow();
      setSyncState((current) => ({ ...current, message: summary.message }));
    } catch (error) {
      console.warn('Household sync will retry automatically.', error);
      setSyncState((current) => ({ ...current, message: 'Saved here. Family sync will retry automatically.' }));
    } finally {
      activeSyncs.current -= 1;
      if (!activeSyncs.current) setSyncState((current) => ({ ...current, syncing: false }));
    }
  }, []);

  useEffect(() => {
    if (demoMode) return;
    const bootstrap = async () => {
      try {
        const configured = await refreshSyncConfig();
        if (configured) await runSync();
      } catch (error) {
        console.warn('Initial family sync will retry automatically.', error);
        setSyncState((current) => ({ ...current, message: 'Saved here. Family sync will retry automatically.' }));
      }
    };
    const syncWhenOnline = () => void runSync();
    const syncWhenVisible = () => {
      if (document.visibilityState === 'visible') void runSync();
    };
    const interval = setInterval(syncWhenVisible, 5 * 60 * 1000);
    addEventListener('online', syncWhenOnline);
    document.addEventListener('visibilitychange', syncWhenVisible);
    void bootstrap();
    return () => {
      clearInterval(interval);
      removeEventListener('online', syncWhenOnline);
      document.removeEventListener('visibilitychange', syncWhenVisible);
    };
  }, [refreshSyncConfig, runSync]);

  const capture = async (photo: Blob) => {
    const item = await createDraft(photo);
    setEditor({ item, mode: 'new' });
    if (demoMode) return;
    void suggestItemNames(item).then((updated) => {
      if (updated) setEditor((current) => current?.item.id === updated.id ? { ...current, item: updated } : current);
    });
  };

  const captureFile = async (file: File | undefined, input: HTMLInputElement | null) => {
    if (!file) return;
    setCaptureState({ busy: true, error: '' });
    try {
      await capture(await normalizePhoto(file));
      setCaptureState({ busy: false, error: '' });
    } catch (error) {
      setCaptureState({ busy: false, error: error instanceof Error ? error.message : 'This photo could not be opened. Try another one.' });
    } finally {
      if (input) input.value = '';
    }
  };

  const openNameReview = () => {
    if (nameReview[0]) setEditor({ item: nameReview[0], mode: 'name-review' });
  };

  const openLocationReview = () => {
    if (locationReview[0]) setEditor({ item: locationReview[0], mode: 'location-review' });
  };

  const toggleGrouping = () => {
    setGrouped((current) => {
      const next = !current;
      try {
        localStorage.setItem('gallery.grouped', next ? '1' : '0');
      } catch {
        // A blocked preference store should not affect the gallery itself.
      }
      return next;
    });
  };

  const advanceReview = async (mode: EditorMode, current: ItemRecord) => {
    if (mode !== 'name-review' && mode !== 'location-review') {
      setEditor(null);
      return;
    }
    const allItems = await db.items.orderBy('createdAt').toArray();
    const next = allItems.find((item) => isAfter(item, current) && (mode === 'name-review' ? !item.name.trim() : Boolean(item.name.trim()) && !item.locationNote.trim()));
    setEditor(next ? { item: next, mode } : null);
  };

  const save = async (item: ItemRecord, values: { name: string; locationNote: string; locationGroup: string; synonyms: string[] }) => {
    const mode = editor?.mode ?? 'edit';
    const saved = await saveItem(item, values);
    await advanceReview(mode, item);
    setSyncState((current) => ({ ...current, message: 'Saved on this device.' }));
    if (demoMode) return;
    void runSync();
    void enrichItem(saved).then((updated) => {
      if (updated) void runSync();
    });
  };

  const remove = async (item: ItemRecord) => {
    const label = item.name ? `“${item.name}”` : 'this unnamed capture';
    if (!confirm(`Delete ${label} from this device?`)) return;
    const mode = editor?.mode ?? 'edit';
    await deleteItem(item);
    await advanceReview(mode, item);
    setSyncState((current) => ({ ...current, message: 'Deleted on this device.' }));
    void runSync();
  };

  const renameSavedPlace = async (kind: PlaceKind, previousName: string, nextName: string) => {
    const count = await renamePlace(kind, previousName, nextName);
    if (count) {
      setSyncState((current) => ({ ...current, message: `Updated ${count} item${count === 1 ? '' : 's'} on this device.` }));
      if (!demoMode) void runSync();
    }
    return count;
  };

  return (
    <div className={`app-shell${editor ? ' editor-open' : ''}`}>
      {screen === 'camera' ? (
        <CameraScreen
          busy={captureState.busy}
          error={captureState.error}
          nameReviewCount={nameReview.length}
          locationReviewCount={locationReview.length}
          onTakePhoto={() => cameraInputRef.current?.click()}
          onChoosePhoto={() => libraryInputRef.current?.click()}
          onFind={() => setScreen('gallery')}
          onReviewNames={openNameReview}
          onReviewLocations={openLocationReview}
        />
      ) : (
        <GalleryScreen
          sections={sections}
          totalItems={namedItems.length}
          nameReviewCount={nameReview.length}
          locationReviewCount={locationReview.length}
          outboxCount={outboxCount}
          query={query}
          syncMessage={syncState.message}
          syncing={syncState.syncing}
          syncConfigured={syncState.configured}
          capabilities={capabilities}
          grouped={grouped}
          onQueryChange={setQuery}
          onOpenCamera={() => cameraInputRef.current?.click()}
          onOpenItem={(item) => setEditor({ item, mode: 'edit' })}
          onOpenSettings={() => setSettingsOpen(true)}
          onReviewNames={openNameReview}
          onReviewLocations={openLocationReview}
          onSync={() => void runSync(true)}
          onToggleGrouping={toggleGrouping}
        />
      )}

      {editor ? <ItemEditor key={editor.item.id} item={editor.item} mode={editor.mode} knownGroups={knownGroups} onClose={() => setEditor(null)} onDelete={remove} onSave={save} onSkip={(item) => advanceReview(editor.mode, item)} onSuggestGroup={suggestLocationGroup} /> : null}
      {settingsOpen ? (
        <SettingsDialog
          capabilities={capabilities}
          install={install}
          items={items}
          onClose={() => setSettingsOpen(false)}
          onDataRestored={() => void runSync()}
          onRenamePlace={renameSavedPlace}
          onClearData={async () => {
            await clearLocalData();
            setSyncState({ configured: true, syncing: false, message: 'Local data was deleted. Family items will restore automatically.' });
          }}
        />
      ) : null}

      <input
        ref={cameraInputRef}
        className="visually-hidden"
        aria-label="Take an item photo"
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(event) => void captureFile(event.target.files?.[0], event.currentTarget)}
      />
      <input
        ref={libraryInputRef}
        className="visually-hidden"
        aria-label="Choose an item photo"
        type="file"
        accept="image/*"
        onChange={(event) => void captureFile(event.target.files?.[0], event.currentTarget)}
      />
    </div>
  );
}

function isAfter(candidate: ItemRecord, current: ItemRecord) {
  return candidate.createdAt > current.createdAt || (candidate.createdAt === current.createdAt && candidate.id > current.id);
}

function initialScreen(): Screen {
  const requested = new URLSearchParams(location.search).get('screen');
  if (requested === 'camera' || requested === 'gallery') return requested;
  return matchMedia('(max-width: 720px) and (pointer: coarse)').matches ? 'camera' : 'gallery';
}

function readGroupingPreference() {
  try {
    return localStorage.getItem('gallery.grouped') === '1';
  } catch {
    return false;
  }
}
